/**
 * transform-booking — de les reserves de l'API (`Booking`) a les cards del calendari v2
 * (`ReservationCellData`). Funcions pures, sense React: els tests les proven directament.
 *
 * Grups de Beds24 (`plans/calendar-visual-refresh.md` §7): la reserva mestra porta `masterId`
 * buit i les altres hi apunten amb el `beds24Id` de la mestra. El grup es compta sobre **totes**
 * les reserves carregades (a la vista «Totes», de totes les propietats), sense les cancel·lades.
 */

import type { Booking, Unit } from '@/services/api';
import type { ReservationCellData } from '@/components/calendar-v2/ReservationCell';
import type { BedType } from '@/components/calendar-v2/BedIcon';
import { needsBedReview } from '@/components/calendar-v2/bed-review';

/**
 * Parseja un JSON string de bedConfig/defaultBedConfig i retorna un array de BedType.
 * Format entrada: [{"type":"double","qty":1},{"type":"crib","qty":1}]
 * Format sortida: ['double', 'crib']  (expandit per qty)
 */
export function parseBedConfig(json: string | null | undefined): BedType[] {
  if (!json) return [];
  try {
    const items = JSON.parse(json) as Array<{ type: string; qty: number }>;
    const result: BedType[] = [];
    const validTypes: BedType[] = ['single', 'double', 'crib', 'bunk', 'extra'];
    for (const item of items) {
      if (validTypes.includes(item.type as BedType)) {
        for (let i = 0; i < (item.qty || 1); i++) {
          result.push(item.type as BedType);
        }
      }
    }
    return result;
  } catch {
    return [];
  }
}

/** Clau del grup de Beds24: el `beds24Id` de la mestra (la mateixa mestra, el seu). */
function groupKey(booking: Booking): string | null {
  return booking.masterId?.trim() || booking.beds24Id || null;
}

/**
 * Mida del grup de Beds24 de cada reserva carregada: `id` de la reserva → nombre de reserves vives
 * del grup (mestra + filles). Només hi surten les de grups de 2 o més; les cancel·lades no compten
 * ni reben mida (no formen part del grup viu).
 */
export function computeGroupSizes(bookings: readonly Booking[]): Map<string, number> {
  const live = bookings.filter((b) => b.status !== 'CANCELLED');

  const countByKey = new Map<string, number>();
  for (const b of live) {
    const key = groupKey(b);
    if (key) countByKey.set(key, (countByKey.get(key) ?? 0) + 1);
  }

  const sizes = new Map<string, number>();
  for (const b of live) {
    const key = groupKey(b);
    const size = key ? (countByKey.get(key) ?? 0) : 0;
    if (size >= 2) sizes.set(b.id, size);
  }
  return sizes;
}

const STATUS_MAP: Record<string, ReservationCellData['status']> = {
  PENDING: 'pending',
  CONFIRMED: 'confirmed',
  CHECKED_IN: 'checked_in',
  CHECKED_OUT: 'checked_out',
  CANCELLED: 'cancelled',
  // Senyals de la card (§6 F1.10): abans queia a 'confirmed'.
  NO_SHOW: 'no_show',
};

/**
 * Transforma Booking (API) → ReservationCellData (Calendar V2).
 * `unitMap` per derivar beds[] amb fallback a defaultBedConfig (Sprint 31); `groupSize` de
 * `computeGroupSizes` (< 2 o absent → no és grup).
 */
export function transformBooking(
  booking: Booking,
  unitMap?: Map<string, Unit>,
  groupSize?: number | null,
): ReservationCellData {
  // 🆕 Sprint 29 — Derivar isPaid des de paymentStatus (no des de balance)
  const paymentStatusRaw = booking.paymentStatus ?? null;
  const isPaid: boolean | null =
    paymentStatusRaw === 'PAID'
      ? true
      : paymentStatusRaw === 'PENDING' || paymentStatusRaw === 'PARTIAL'
        ? false
        : null; // null = desconegut (camp no present o REFUNDED)

  // 🛏️ Sprint 31 — Derivar beds[] des de bedConfig (reserva) o defaultBedConfig (unit)
  const unitId = booking.unitId || booking.roomId;
  const unit = unitMap?.get(unitId || '');
  const resolvedBedConfig = booking.bedConfig ?? unit?.defaultBedConfig ?? null;
  const beds = parseBedConfig(resolvedBedConfig);

  const group = groupSize != null && groupSize >= 2 ? groupSize : null;

  return {
    id: booking.id,
    beds24Id: booking.beds24Id,
    guestName: booking.guestName || 'Sense nom',
    channel: booking.channel || 'Direct',
    checkIn: booking.checkIn,
    checkOut: booking.checkOut,
    unitId: unitId, // Fallback a roomId si no hi ha unitId
    guestsRegistered: booking.guestsRegistered || 0,
    guestsExpected: booking.totalGuests || booking.numAdults + booking.numChildren,
    numAdults: booking.numAdults,
    numChildren: booking.numChildren,
    primaryGuestName: booking.primaryGuestName || null,
    companyName: booking.billingClientName || booking.guestCompany || undefined,
    status: STATUS_MAP[booking.status] || 'confirmed',
    // TTLock: encara no hi ha dades de codis a la reserva; «Pendent» fins que n'hi hagi
    // ('error' feia sortir «Sense codi» a totes les reserves).
    keyStatus: 'pending',
    // Grup de Beds24 (§7): la card pinta «G{n}» amb `groupSize`
    groupSize: group,
    isGroup: group !== null,
    // 🛏️ Sprint 31 — Llits i mascota
    beds: beds.length > 0 ? beds : undefined,
    bedConfigRaw: booking.bedConfig ?? null,
    // 🛏️ Runbook 10 — marca automàtica, preferència i avís (C5)
    bedConfigSource: booking.bedConfigSource ?? null,
    bedPreference: booking.bedPreference ?? null,
    bedWarning: booking.bedWarning ?? null,
    // 🛏️ Runbook 20 — «Llits per revisar» el decideix el backend (absent = no)
    bedReviewPending: booking.bedReviewPending ?? false,
    hasPet: booking.hasPet ?? false,
    // 🆕 Sprint 29 — Pagament real
    isPaid,
    paymentStatusRaw,
    paymentMethod: booking.paymentMethod ?? null,
    balance: booking.balance ?? null,
    deposit: booking.deposit ?? null,
    paidAt: booking.paidAt ?? null,
    totalPrice: booking.totalPrice ?? null,
    // 🆕 Info canal Beds24 (readonly al Sheet)
    referer: booking.referer || null,
    apiSource: booking.apiSource || null,
    lang: booking.lang || null,
    guestPhone: booking.guestPhone || null,
    guestPhoneE164: booking.guestPhoneE164 ?? null,
    beds24PropertyId: booking.beds24PropertyId ?? null,
    guestCountry: booking.guestCountry || null,
    guestCountry2: booking.guestCountry2 || null,
    // 🆕 Sprint 27 — Camps addicionals
    numNights: booking.numNights ?? null,
    arrivalTime: booking.arrivalTime ?? null,
    whatsappSent: booking.hasWhatsapp ?? null,
    // Senyals de la card (`plans/calendar-card-signals.md` §2), si el backend els envia.
    ...(booking.signals ? { signals: booking.signals } : {}),
  };
}

/**
 * Les reserves carregades → cards de la graella. El grup es calcula abans de treure les
 * cancel·lades (amb «Veure cancel·lades» desactivat no surten, però tampoc comptaven).
 */
export function bookingsToReservations(
  bookings: readonly Booking[],
  unitMap: Map<string, Unit>,
  showCancelled: boolean,
): ReservationCellData[] {
  const groupSizes = computeGroupSizes(bookings);
  return bookings
    .filter((b) => showCancelled || b.status !== 'CANCELLED')
    .map((b) => transformBooking(b, unitMap, groupSizes.get(b.id) ?? null));
}

/** «Llits per revisar» (runbook 10) d'una reserva viva: les cancel·lades no es revisen mai. */
export function isLiveBedToReview(r: ReservationCellData): boolean {
  return r.status !== 'cancelled' && needsBedReview(r);
}

/** Comptador del botó «Llits per revisar»: sense les cancel·lades, encara que es mostrin (P16). */
export function countBedsToReview(reservations: readonly ReservationCellData[]): number {
  return reservations.filter(isLiveBedToReview).length;
}
